<template>
    <KeepAlive>
        <component
			:is="asyncComponent"
			v-bind="$props"
			class="
				flex justify-center items-center gap-2
				transition-all duration-150
				focus-visible:!outline-2
				focus-visible:!outline-transparent
				focus-visible:!ring-4
				focus-visible:!ring-blue-500
				focus:outline-blue-500
			"
			:class="sizes[size] + (loadingAbsolute ? ' relative' : '')"
		>
            <template v-for="(_, name) in $slots" #[name]>
                <slot :name="name"></slot>
            </template>
			<template #right-icon>
				<i
					class="fa-solid fa-spinner text-[1.3em]"
					:class="{
						[`absolute ${loadingRightOffset}`]: loadingAbsolute
					}"
					v-if="loading"
				></i>
				<slot name="right-icon" v-else></slot>
			</template>
        </component>
    </KeepAlive>
</template>

<script setup lang="ts">
import { PropType, defineOptions, defineProps, defineAsyncComponent, computed, ref } from 'vue'

defineOptions({
    name: 'v-button'
})

const sizes = ref({
	'x-small': 'px-3 min-h-8 rounded-lg !text-xs',
	'xs/s': 'px-[13px] min-h-9 rounded-[9px] !text-sm',
	small: 'px-[14px] min-h-10 rounded-[10px] !text-base',
	medium: 'px-4 min-h-12 rounded-xl !text-lg',
});

type modeType = 'primary' | 'secondary' | 'dark' | 'tertiary' | 'danger' | 'ghost'
type sizeType = 'x-small' | 'small' | 'medium'

const props = defineProps({
	mode: {
		type: String as PropType<modeType>,
		default: 'primary'
	},
	size: {
		type: String as PropType<sizeType>,
		default: 'x-small'
	},
	text: {
		type: String,
		default: undefined
	},
	disabled: {
		type: Boolean,
		default: false
	},
	focusBg: {
		type: Boolean,
		default: false
	},
	loading: {
		type: Boolean,
		default: false
	},
	loadingAbsolute: {
		type: Boolean,
		default: false
	},
});

const loadingRightOffset = computed(() => ({
	'x-small': 'right-3',
	'xs/s': 'right-[13px]',
	small: 'right-[14px]',
	medium: 'right-4',
})[props.size])

const capitalizedMode = computed(() => props.mode?.toCapitalize?.())

const asyncComponent = defineAsyncComponent(async () => {
	const { default: component } = await import(`./${capitalizedMode.value}Button.vue`);
	return component;
});

</script>